import type { PickupAvailabilitySlot } from '../../../../../core/api/pickup/models/pickup.model';

/**
 * Customer-facing availability notice kinds under
 * `createShipment.serviceStep.pickupDropOff.availability.*`.
 *
 * Weekend and holiday eligibility remain Salesforce-owned; ESO Lite does not select
 * Sunday- or holiday-specific notice kinds.
 */
export type PickupAvailabilityNoticeKind =
  'sameDayCutoff' | 'notSupportedAtAddress' | 'temporaryUnavailable' | 'serviceUnavailable';

/** Result of mapping validate outcomes to UI notice + whether date radios should render. */
export interface PickupAvailabilityNoticeResult {
  kind: PickupAvailabilityNoticeKind | null;
  showDates: boolean;
}

/** Inputs for mapping validate outcomes to a customer notice (UI heuristics only). */
export interface MapPickupAvailabilityNoticeInput {
  pickUps: PickupAvailabilitySlot[];
  errorMessage: string | null;
  /** Local calendar date as `yyyy-MM-dd`. */
  todayIso: string;
  /** True when `POST /pickup/validate` rejected (HTTP / transport failure). */
  validateRejected: boolean;
}

const PICKUP_ERROR_01 = 'PICKUP_ERROR_01';
const PICKUP_ERROR_02 = 'PICKUP_ERROR_02';

/**
 * Maps validate `pickUps` / `errorMessage` into a customer notice kind without exposing
 * raw Salesforce or BE codes. Does not infer weekend or holiday reasons locally.
 */
export function mapPickupAvailabilityNotice(
  input: MapPickupAvailabilityNoticeInput,
): PickupAvailabilityNoticeResult {
  if (input.validateRejected) {
    return { kind: 'serviceUnavailable', showDates: false };
  }

  if (input.pickUps.length === 0) {
    if (input.errorMessage === PICKUP_ERROR_01) {
      return { kind: 'notSupportedAtAddress', showDates: false };
    }
    if (input.errorMessage === PICKUP_ERROR_02) {
      return { kind: 'temporaryUnavailable', showDates: false };
    }
    return { kind: 'temporaryUnavailable', showDates: false };
  }

  const includesToday = input.pickUps.some((slot) => slot.date === input.todayIso);
  if (!includesToday) {
    return { kind: 'sameDayCutoff', showDates: true };
  }

  return { kind: null, showDates: true };
}

/**
 * i18n key for a notice kind under `createShipment.serviceStep.pickupDropOff.availability`.
 */
export function pickupAvailabilityNoticeI18nKey(kind: PickupAvailabilityNoticeKind): string {
  return `createShipment.serviceStep.pickupDropOff.availability.${kind}`;
}
